"use client";

import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { erc20Abi, type Hex } from "viem";
import {
  useAccount,
  usePublicClient,
  useSendTransaction,
  useSwitchChain,
  useWriteContract,
} from "wagmi";
import { useWallet as useSolanaWallet } from "@solana/wallet-adapter-react";
import {
  BRIDGE_CHAINS,
  fetchBridgeQuote,
  type BridgeChain,
  type BridgeQuote,
  type BridgeToken,
} from "@/lib/bridge-api";
import { useSession } from "@/lib/useSignIn";

// Bridge USDT/USDC between chains, non-custodially: our backend quotes via LI.FI, the
// user's own wallet signs everything. v1 executes EVM-source routes (approve → bridge tx);
// Solana-source routes quote but point the user to execute from a Solana wallet (different
// signing path, later).

type Phase =
  | { step: "idle" }
  | { step: "switching" }
  | { step: "approving"; hash?: string }
  | { step: "bridging"; hash?: string }
  | { step: "done"; hash: string }
  | { step: "error"; message: string };

const fmtEta = (s: number) =>
  s < 90 ? `${s}s` : s < 5400 ? `${Math.round(s / 60)} min` : `${(s / 3600).toFixed(1)} h`;

const short = (a: string) => (a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a);

const BridgePanel = () => {
  const [fromChain, setFromChain] = useState<BridgeChain>("POLYGON");
  const [toChain, setToChain] = useState<BridgeChain>("SOLANA");
  const [token, setToken] = useState<BridgeToken>("USDT");
  const [amount, setAmount] = useState("");
  const [toAddressManual, setToAddressManual] = useState("");
  const [fromAddressManual, setFromAddressManual] = useState("");
  const [phase, setPhase] = useState<Phase>({ step: "idle" });

  const { isSignedIn } = useSession();
  const { address: evmAddress, chainId: connectedChainId } = useAccount();
  const solana = useSolanaWallet();
  const { switchChainAsync } = useSwitchChain();
  const { writeContractAsync } = useWriteContract();
  const { sendTransactionAsync } = useSendTransaction();
  const publicClient = usePublicClient();

  const fromMeta = BRIDGE_CHAINS.find((c) => c.id === fromChain)!;
  const toMeta = BRIDGE_CHAINS.find((c) => c.id === toChain)!;

  // Destination address: same EVM address for EVM→EVM, the connected Solana wallet for
  // →Solana, else manual. Always overridable, people bridge to other wallets.
  const autoToAddress = useMemo(() => {
    if (toMeta.evm) return evmAddress ?? "";
    return solana.publicKey?.toBase58() ?? "";
  }, [toMeta.evm, evmAddress, solana.publicKey]);
  const toAddress = toAddressManual || autoToAddress;

  // Source address must match the SOURCE chain's family, quoting a Polygon route with a
  // Solana fromAddress is exactly the mistake LI.FI rejects. Wallet first; manual entry as
  // a quote-only fallback (execution always requires the wallet).
  const autoFromAddress = fromMeta.evm ? (evmAddress ?? "") : (solana.publicKey?.toBase58() ?? "");
  const fromAddress = autoFromAddress || fromAddressManual;

  const quote = useMutation({
    mutationFn: () =>
      fetchBridgeQuote({
        from_chain: fromChain,
        to_chain: toChain,
        token,
        amount: Number(amount),
        from_address: fromAddress,
        to_address: toAddress,
      }),
    onMutate: () => setPhase({ step: "idle" }),
  });

  const q: BridgeQuote | undefined = quote.data;
  const canQuote =
    isSignedIn && Number(amount) > 0 && !!toAddress && !!fromAddress && fromChain !== toChain;
  const executable = q && fromMeta.evm && !!q.transaction_request && !!evmAddress;

  const execute = async () => {
    if (!q || !q.transaction_request || !evmAddress || !publicClient) return;
    try {
      const chainId = q.source.chain_id;
      // 1. Be on the source chain.
      if (chainId && connectedChainId !== chainId) {
        setPhase({ step: "switching" });
        await switchChainAsync({ chainId });
      }

      // 2. ERC-20 allowance for the bridge spender (skip if already sufficient, a second
      //    approve would just cost gas).
      if (q.approval_address) {
        const allowance = await publicClient.readContract({
          address: q.source.token_address as Hex,
          abi: erc20Abi,
          functionName: "allowance",
          args: [evmAddress, q.approval_address as Hex],
        });
        if (allowance < BigInt(q.source.amount_minor)) {
          setPhase({ step: "approving" });
          const approveHash = await writeContractAsync({
            address: q.source.token_address as Hex,
            abi: erc20Abi,
            functionName: "approve",
            // Exact amount, not infinite: a compromised bridge contract can then take at
            // most this transfer, not the user's whole balance forever.
            args: [q.approval_address as Hex, BigInt(q.source.amount_minor)],
          });
          setPhase({ step: "approving", hash: approveHash });
          await publicClient.waitForTransactionReceipt({ hash: approveHash });
        }
      }

      // 3. The bridge transaction LI.FI prepared.
      setPhase({ step: "bridging" });
      const tx = q.transaction_request;
      const hash = await sendTransactionAsync({
        to: tx.to as Hex,
        data: tx.data as Hex,
        value: tx.value ? BigInt(tx.value) : undefined,
      });
      setPhase({ step: "bridging", hash });
      await publicClient.waitForTransactionReceipt({ hash });
      setPhase({ step: "done", hash });
    } catch (e) {
      const msg = (e as Error).message ?? "";
      setPhase(
        /reject|denied|cancel/i.test(msg)
          ? { step: "idle" }
          : { step: "error", message: msg.slice(0, 140) }
      );
    }
  };

  const sel =
    "rounded-md border border-line bg-page px-2 py-2 text-sm text-fg outline-none focus:border-brand";

  return (
    <div className="mx-auto flex w-full max-w-md flex-col gap-4 rounded-lg border border-line bg-surface p-4">
      <div>
        <h1 className="text-sm font-semibold text-fg">Bridge</h1>
        <p className="mt-0.5 text-xs text-fg-muted">
          Move {token} between chains. Get Quotes; your wallet signs everything, funds
          never pass through Dubu.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-fg-muted">From</span>
          <select value={fromChain} onChange={(e) => setFromChain(e.target.value as BridgeChain)} className={sel}>
            {BRIDGE_CHAINS.map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-fg-muted">To</span>
          <select value={toChain} onChange={(e) => setToChain(e.target.value as BridgeChain)} className={sel}>
            {BRIDGE_CHAINS.filter((c) => c.id !== fromChain).map((c) => (
              <option key={c.id} value={c.id}>{c.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="grid grid-cols-[1fr_110px] gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-fg-muted">Amount</span>
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
            placeholder="0.00"
            className={`tnum ${sel}`}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-fg-muted">Token</span>
          <select value={token} onChange={(e) => setToken(e.target.value as BridgeToken)} className={sel}>
            <option value="USDT">USDT</option>
            <option value="USDC">USDC</option>
          </select>
        </label>
      </div>

      {!autoFromAddress && (
        <label className="flex flex-col gap-1">
          <span className="text-[11px] text-fg-muted">
            Source address on {fromMeta.label} (no {fromMeta.evm ? "EVM" : "Solana"} wallet
            connected, quotes only)
          </span>
          <input
            value={fromAddressManual}
            onChange={(e) => setFromAddressManual(e.target.value.trim())}
            placeholder={fromMeta.evm ? "0x…" : "Solana address"}
            className={`tnum ${sel}`}
          />
        </label>
      )}

      <label className="flex flex-col gap-1">
        <span className="text-[11px] text-fg-muted">
          Destination address on {toMeta.label}
          {autoToAddress && !toAddressManual && " (your connected wallet)"}
        </span>
        <input
          value={toAddressManual || autoToAddress}
          onChange={(e) => setToAddressManual(e.target.value.trim())}
          placeholder={toMeta.evm ? "0x…" : "Solana address"}
          className={`tnum ${sel}`}
        />
      </label>

      <button
        type="button"
        disabled={!canQuote || quote.isPending}
        onClick={() => quote.mutate()}
        className="rounded-md bg-raised py-2.5 text-sm text-fg transition-colors hover:bg-line disabled:opacity-40"
      >
        {quote.isPending ? "Getting quote…" : "Get quote"}
      </button>

      {!isSignedIn && (
        <p className="text-center text-xs text-fg-muted">Connect and sign in to bridge.</p>
      )}
      {quote.isError && (
        <p className="text-xs text-sell">{(quote.error as Error).message}</p>
      )}

      {q && (
        <div className="flex flex-col gap-1.5 rounded-md bg-raised p-3 text-xs">
          <div className="flex justify-between">
            <span className="text-fg-muted">You send</span>
            <span className="tnum text-fg">{q.from.amount} {q.from.token} · {fromMeta.label}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-fg-muted">You receive (est.)</span>
            <span className="tnum text-fg">{q.to.amount} {q.to.token} · {toMeta.label}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-fg-muted">Minimum received</span>
            <span className="tnum text-fg-secondary">{q.to.amount_min}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-fg-muted">Fees / gas</span>
            <span className="tnum text-fg-secondary">${q.fees_usd} / ${q.gas_usd}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-fg-muted">Route / ETA</span>
            <span className="text-fg-secondary">{q.tool} · {fmtEta(q.eta_seconds)}</span>
          </div>

          {executable ? (
            <button
              type="button"
              onClick={() => void execute()}
              disabled={phase.step !== "idle" && phase.step !== "error"}
              className="mt-2 rounded-md bg-brand py-2.5 text-sm font-medium text-black transition-colors hover:bg-brand-glow disabled:opacity-40"
            >
              {phase.step === "switching" && "Switching network…"}
              {phase.step === "approving" && "Approve in wallet…"}
              {phase.step === "bridging" && "Confirm bridge in wallet…"}
              {(phase.step === "idle" || phase.step === "error") && `Bridge ${q.from.amount} ${token}`}
              {phase.step === "done" && "Bridged ✓"}
            </button>
          ) : (
            <p className="mt-2 text-fg-muted">
              {fromMeta.evm
                ? "Connect an EVM wallet to execute this route."
                : "Solana-source routes: execute from your Solana wallet, one-click execution here is coming soon."}
            </p>
          )}

          {(phase.step === "approving" || phase.step === "bridging") && phase.hash && (
            <p className="tnum break-all text-fg-muted">tx {short(phase.hash)}, waiting for confirmation…</p>
          )}
          {phase.step === "done" && (
            <p className="text-buy">
              Source transaction confirmed, {q.tool} is delivering to {short(toAddress)} (~{fmtEta(q.eta_seconds)}).{" "}
              <a
                href={`https://scan.li.fi/tx/${phase.hash}`}
                target="_blank"
                rel="noreferrer"
                className="underline"
              >
                Track on LI.FI
              </a>
            </p>
          )}
          {phase.step === "error" && <p className="text-sell">{phase.message}</p>}
        </div>
      )}
    </div>
  );
};

export default BridgePanel;
